import { Box } from '@stitch-fix/mode-react';
import { A, D, F, G, pipe } from '@mobily/ts-belt';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import { FilterQueryParam, isValidFilter, sortQueryParam, UseQueryParamReturn, useSearchParams } from '../../utils/hooks/useSearchParams';
import { formatFiltersVariable } from '../../utils/helpers';
import { FilterMenu } from './FilterMenu';
import { SortMenu } from './SortMenu';
import styles from './menu.module.scss';
import { trackEvent, SearchSubmitClick, SEARCH_RESULTS_EVENT_CATEGORY } from '../../utils/events';
import { FilterFragment, SortFragment } from '../../utils/hooks/getSearchResults';
import { useGetNewValidFiltersLazyQuery, GetNewValidFiltersQuery, SelectedFilterFragment } from '../../utils/hooks/getNewValidFilters';
type ControlMenuProps = {
  filters?: FilterFragment[];
  sortOptions?: SortFragment[];
  queryId: string;
};
const trackViewResultsClick = (queryId: string) => {
  const event: SearchSubmitClick = {
    action_name: 'search_submit',
    action_type: 'click',
    canonical_name: SEARCH_RESULTS_EVENT_CATEGORY,
    event_name: SEARCH_RESULTS_EVENT_CATEGORY,
    additional_context: {
      direct_buy: {
        previous_query_id: queryId
      }
    }
  };
  trackEvent(event);
};
const getSelectedValues = (filterOptions: SelectedFilterFragment['options'] = []) => pipe(filterOptions, A.filter(option => option.selected), A.map(option => option.value), F.toMutable);
export const getSelectedFilters = (filters: SelectedFilterFragment[] = []) => {
  const colorFilter = filters.find(filter => filter.id === 'color');
  const occasionFilter = filters.find(filter => filter.id === 'occasion');
  return {
    color: getSelectedValues(colorFilter?.options),
    occasion: getSelectedValues(occasionFilter?.options)
  };
};

/**
 * Because filters are dynamic, the Graph returns a different list of filter options depending
 * on the filters selected. We use the `GetNewValidFiltersQuery` query to get the **next** available
 * filter options after a client selects new filter options and clicks "View results".
 *
 * Since the client application has no knowledge of the server's state, we are validating the next URL
 * before transitioning to it; this prevents persisting unavailable filter options in the URL query params.
 * Additionally, the query params may not reflect the current filters selected state on the server when the
 * page **initially** loads, but are then synced once the client applies new filters and clicks "View results".
 */
const handleNewFiltersRedirect = (data: GetNewValidFiltersQuery, setQueryParams: UseQueryParamReturn['setQueryParams']) => {
  const newSelectedFilters = getSelectedFilters(data?.shopContent?.search?.filters);
  setQueryParams(newSelectedFilters);
};

/**
 * `ControlMenu` renders the Filter and Sort Menu.
 * The `ControlMenu` component determines the currently selected filters and sort options.
 * It also handles redirecting clients to the new search results when selecting new filters or sort options.
 */
const ControlMenu = ({
  filters,
  sortOptions,
  queryId
}: ControlMenuProps) => {
  const {
    queryParams,
    setQueryParam,
    setQueryParams
  } = useSearchParams();
  const [validateNewFilters, {
    loading: isLoadingNewFilters
  }] = useGetNewValidFiltersLazyQuery({
    onCompleted: data => {
      handleNewFiltersRedirect(data, setQueryParams);
    }
  });
  const {
    reportSelectEvent
  } = useSelectEvent();
  const validFilters = filters?.filter(isValidFilter);
  const hasValidFilters = G.isArray(validFilters) && A.isNotEmpty(validFilters);
  const hasSortOptions = G.isArray(sortOptions) && A.isNotEmpty(sortOptions);
  const hasMenuOptions = hasValidFilters && hasSortOptions;
  if (!hasMenuOptions) return null;
  const onViewResultsFilterClick = (filterId: FilterQueryParam, selectedValues: string[]) => {
    const newSelectedFilters = pipe(queryParams, D.selectKeys(['color', 'occasion']), D.merge({
      [filterId]: selectedValues
    }));
    if (G.isString(queryParams.q)) {
      validateNewFilters({
        variables: {
          q: queryParams.q,
          filters: formatFiltersVariable(newSelectedFilters)
        }
      });
      trackViewResultsClick(queryId);
      reportSelectEvent({
        schema: 'select',
        name: 'view_results',
        action_name: 'filter_results'
      });
    }
  };
  return <Box className={styles['menu-wrapper']}>
      {validFilters.map(({
      id,
      label,
      options,
      menuDisplayTemplate
    }) => <FilterMenu
    // After selecting new values and clicking 'View Results',
    // FilterMenu maintains its state so the new values are shown unselected.
    // Update the `key` value to reset the component's state: https://react.dev/learn/preserving-and-resetting-state#option-2-resetting-state-with-a-key
    key={queryParams[id]?.join('-') || id} id={id} label={label} options={options} initialSelectedValues={queryParams[id]} onViewResultsClick={onViewResultsFilterClick} menuDisplayTemplate={menuDisplayTemplate} isViewResultsDisabled={isLoadingNewFilters} onResetClick={() => setQueryParam(id, [])} />)}

      <SortMenu
    // After selecting a new sort value and clicking 'View Results',
    // SortMenu maintains its state so the new sort value shown unselected.
    // Update the `key` value to reset the component's state: https://react.dev/learn/preserving-and-resetting-state#option-2-resetting-state-with-a-key
    key={queryParams[sortQueryParam]} initialSortValue={queryParams[sortQueryParam]} sortOptions={sortOptions} onViewResultsClick={(sortId, selectedValue) => {
      trackViewResultsClick(queryId);
      reportSelectEvent({
        schema: 'select',
        name: 'view_results',
        action_name: 'sort_results'
      });
      setQueryParam(sortId, selectedValue);
    }} onResetClick={() => setQueryParam(sortQueryParam, '')} />
    </Box>;
};
export default ControlMenu;